import React from "react";
import { Input, Select, SelectOption, Switch } from "@showitinc/showit-wc";
import AppConstants from "../../constants/AppConstants";
import { MIXED } from "../../constants/DesignConstants.mjs";
import { initFormatter } from "../../utils/PropertyFormatters";

const { UPDATE_ACTION_DELETE, UPDATE_ACTION_SET } = AppConstants;

/** Lottie playback mode options */
const LOTTIE_MODE_FORWARD = "forward";
const LOTTIE_MODE_REVERSE = "reverse";
const LOTTIE_MODE_BOUNCE = "bounce";
const LOTTIE_MODE_REVERSE_BOUNCE = "reverse-bounce";

const MODE_OPTIONS = [
  { value: LOTTIE_MODE_FORWARD, label: "Forward" },
  { value: LOTTIE_MODE_REVERSE, label: "Reverse" },
  { value: LOTTIE_MODE_BOUNCE, label: "Bounce" },
  { value: LOTTIE_MODE_REVERSE_BOUNCE, label: "Reverse Bounce" },
];

/**
 * Shared playback controls for Lottie elements (mode, loop, speed).
 * Used by both LottieAssetSettings and LottieAssetLinkSettings.
 *
 * @param {Object} props
 * @param {Map} props.element - The Lottie element data
 * @param {boolean} props.isLocked - Whether the element is locked
 * @param {Function} props.onPropChange - Callback for property changes
 */
const LottiePlaybackControls = ({ element, isLocked, onPropChange }) => {
  const _onSwitch = (propInfo) => {
    const value = propInfo.value ? 1 : 0;
    onPropChange({
      ...propInfo,
      value,
      action: UPDATE_ACTION_SET,
    });
  };

  const _onNumberChange = (propInfo) => {
    const { value } = propInfo;
    const actualValue = value && typeof value === "object" ? value.value : value;
    const parsedValue = parseFloat(actualValue);
    const finalValue = Number.isNaN(parsedValue) ? actualValue : parsedValue;

    onPropChange({
      ...propInfo,
      value: finalValue,
      action: UPDATE_ACTION_SET,
    });
  };

  const _onSelectChange = (propInfo) => {
    const { value, defaultValue } = propInfo;
    const actualValue = value?.detail?.value ?? value;

    onPropChange({
      ...propInfo,
      value: actualValue,
      action: actualValue === defaultValue ? UPDATE_ACTION_DELETE : UPDATE_ACTION_SET,
    });
  };

  const separatePath = (path) => path.split(".");
  const getValue = (path, fallback) => element.getIn(separatePath(path), fallback);
  const prepChange = (path, defaultValue) => [path, getValue(path), true, false, defaultValue];

  const modeValue = getValue("content.mode", LOTTIE_MODE_FORWARD);
  const speedValue = getValue("content.speed", 1);

  const modeMixed = modeValue === MIXED;
  const speedMixed = speedValue === MIXED;

  const { onChange: onSwitch } = initFormatter(_onSwitch);
  const { onChange: onNumberChange } = initFormatter(_onNumberChange);
  const { onChange: onSelectChange } = initFormatter(_onSelectChange);

  return (
    <>
      <Switch
        data-testid="lottie-loop-switch"
        checked={Boolean(getValue("content.loop", 1))}
        disabled={isLocked}
        label="Loop"
        onSIChange={onSwitch(...prepChange("content.loop", 1))}
        reserveContext
      />
      <Select
        data-testid="lottie-mode-select"
        disabled={isLocked}
        label="Mode"
        mixed={modeMixed}
        value={modeValue}
        onSISelect={onSelectChange(...prepChange("content.mode", LOTTIE_MODE_FORWARD))}
        reserveContext
        shift="left"
      >
        {modeMixed && <SelectOption value={MIXED}>Mixed</SelectOption>}
        {MODE_OPTIONS.map(({ value, label }) => (
          <SelectOption key={value} value={value}>
            {label}
          </SelectOption>
        ))}
      </Select>
      <Input
        data-testid="lottie-speed-input"
        disabled={isLocked}
        label="Speed"
        mixed={speedMixed}
        type="number"
        min="0.1"
        max="10"
        step="0.1"
        units="x"
        value={speedMixed ? "" : speedValue}
        onSIBeforeChange={onNumberChange(...prepChange("content.speed", 1))}
        onSIInput={onNumberChange(...prepChange("content.speed", 1), false)}
      />
    </>
  );
};

export default LottiePlaybackControls;
